export interface OwnedBuildHubLike {
    IsPersonalWorkspace?: boolean | null;
}

/**
 * Subset of the Me type that is sufficient to decide whether the post-signup
 * onboarding gate should be bypassed. Kept structural so call sites can pass
 * either the generated `Me` (which has both fields), the legacy shape (only
 * `OwnedBuildHubs`), or a stale Apollo cache mid-deploy.
 */
export interface BuildHubAccessLike {
    OwnedBuildHubs?: readonly OwnedBuildHubLike[] | null;
    HasNonPersonalBuildHubAccess?: boolean | null;
}

/**
 * Returns only owned Build Hubs that represent real projects, excluding
 * personal workspaces that are created for mood board usage.
 */
export const getNonPersonalOwnedBuildHubs = <T extends OwnedBuildHubLike>(
    ownedBuildHubs?: readonly T[] | null,
): T[] => {
    return (ownedBuildHubs ?? []).filter(
        (buildHub) => !buildHub.IsPersonalWorkspace,
    );
};

/**
 * Counts only non-personal owned Build Hubs so onboarding can ignore
 * personal workspaces.
 */
export const getNonPersonalOwnedBuildHubCount = (
    ownedBuildHubs?: readonly OwnedBuildHubLike[] | null,
): number => {
    return getNonPersonalOwnedBuildHubs(ownedBuildHubs).length;
};

/**
 * Whether the member owns at least one non-personal Build Hub.
 */
export const hasNonPersonalOwnedBuildHubs = (
    ownedBuildHubs?: readonly OwnedBuildHubLike[] | null,
): boolean => {
    return getNonPersonalOwnedBuildHubCount(ownedBuildHubs) > 0;
};

/**
 * Whether the member has access to at least one non-personal Build Hub —
 * either as an owner or as a collaborator. Drives the post-signup onboarding
 * gate so collaborator-only members (e.g. HDE guests stitched in via
 * `claimInvitedBuildHubCollaborators`) skip create-project and land on the
 * BuildHub their invite pointed at.
 *
 * The server-side `HasNonPersonalBuildHubAccess` boolean on `Me` is the source
 * of truth. We fall back to `OwnedBuildHubs` so a stale Apollo cache during a
 * rolling deploy still gets at least the legacy owner-only behaviour.
 */
export const hasNonPersonalBuildHubAccess = (
    user?: BuildHubAccessLike | null,
): boolean => {
    if (!user) {
        return false;
    }

    if (user.HasNonPersonalBuildHubAccess === true) {
        return true;
    }

    return hasNonPersonalOwnedBuildHubs(user.OwnedBuildHubs);
};
